import { ProfileTracker, PROFILE_TRACKER_STAGE_STATUS } from '../../profile/types/profile-tracker.model';
import { PROFILE_STAGES } from '../../profile/types/profile-stages.model';
import { FULL_ACCESS_MEMBERSHIP_STATUS_IDS } from '../../profile/types/membership-type-ids.model';
import { UserMembership } from '../../profile/types/user-membership.model';

export type UserAccessState = 'ONBOARDING' | 'APPLICATION' | 'BLOCKED' | 'ALLOWED';

/** Ordered membership-application steps (stage id + route slug). */
export const MEMBERSHIP_APPLICATION_STAGES: ReadonlyArray<{ stage: PROFILE_STAGES; slug: string }> = [
  { stage: PROFILE_STAGES.APPLICATION_START, slug: 'application-start' },
  { stage: PROFILE_STAGES.APPLICATION_QUESTIONS, slug: 'application-questions' },
  { stage: PROFILE_STAGES.APPLICATION_RANKING, slug: 'application-ranking' },
  { stage: PROFILE_STAGES.APPLICATION_REVIEW, slug: 'application-review' },
  { stage: PROFILE_STAGES.APPLICATION_COMPLETE, slug: 'application-complete' },
];

export function getApplicationStageIdFromSlug(slug: string): PROFILE_STAGES | undefined {
  const entry = MEMBERSHIP_APPLICATION_STAGES.find((s) => s.slug === slug);
  return entry?.stage;
}

export function getMembershipApplicationSlugIndex(slug: string): number {
  return MEMBERSHIP_APPLICATION_STAGES.findIndex((s) => s.slug === slug);
}

/** Same pattern as PROFILE_STAGES: fixed ids aligned with backend type tables. */
export function getAllowedFullAccessMembershipStatusIds(): number[] {
  return [...FULL_ACCESS_MEMBERSHIP_STATUS_IDS];
}

/** Resolve the single membership object from profile/auth user (API: `membership` or legacy `basic_membership`). */
export function getMembershipFromUser(user: {
  membership?: UserMembership | null;
  basic_membership?: UserMembership | null;
} | null | undefined): UserMembership | null | undefined {
  if (!user) {
    return undefined;
  }
  return user.membership ?? user.basic_membership ?? undefined;
}

/**
 * Numeric FK for membership status from the membership row (prefers scalar `membership_status`).
 */
export function getMembershipStatusId(membership: UserMembership | null | undefined): number | undefined {
  if (membership == null) {
    return undefined;
  }
  if (typeof membership.membership_status === 'number' && !Number.isNaN(membership.membership_status)) {
    return membership.membership_status;
  }
  const nested = membership.membership_status_detail;
  if (nested && (nested.id != null || nested.value != null)) {
    return Number(nested.id ?? nested.value);
  }
  return undefined;
}

/**
 * Tracker row for a given stage id, if present.
 */
export function getTrackerRowForStage(
  tracker: ProfileTracker[] | null | undefined,
  stageId: number
): ProfileTracker | undefined {
  if (!tracker?.length) {
    return undefined;
  }
  const sid = Number(stageId);
  return tracker.find((t) => Number(t.stage) === sid);
}

/**
 * Main onboarding (steps 1–17) is complete when `FINAL_CONFIRMATION` is completed.
 */
export function isMainOnboardingComplete(tracker: ProfileTracker[] | null | undefined): boolean {
  const row = getTrackerRowForStage(tracker, PROFILE_STAGES.FINAL_CONFIRMATION);
  return row?.status === PROFILE_TRACKER_STAGE_STATUS.COMPLETED;
}

/**
 * Membership application phase (18–22) is complete when `APPLICATION_COMPLETE` is completed.
 */
export function isApplicationPhaseComplete(tracker: ProfileTracker[] | null | undefined): boolean {
  const row = getTrackerRowForStage(tracker, PROFILE_STAGES.APPLICATION_COMPLETE);
  return row?.status === PROFILE_TRACKER_STAGE_STATUS.COMPLETED;
}

/**
 * Legacy name: “onboarding” for the membership wall meant both main profile onboarding and
 * application phase. Prefer `isMainOnboardingComplete` / `isApplicationPhaseComplete`.
 * @deprecated Use `isMainOnboardingComplete` and `isApplicationPhaseComplete` explicitly.
 */
export function isOnboardingTrackerComplete(tracker: ProfileTracker[] | null | undefined): boolean {
  return isMainOnboardingComplete(tracker) && isApplicationPhaseComplete(tracker);
}

/**
 * First application-phase slug the user still needs (in order), or `undefined` if none left.
 */
export function resolveNextMembershipApplicationSlug(
  tracker: ProfileTracker[] | null | undefined
): string | undefined {
  for (const { stage, slug } of MEMBERSHIP_APPLICATION_STAGES) {
    const row = getTrackerRowForStage(tracker, stage);
    if (!row || row.status !== PROFILE_TRACKER_STAGE_STATUS.COMPLETED) {
      return slug;
    }
  }
  return undefined;
}

/**
 * Single source of truth for membership wall.
 * `allowedFullAccessStatusIds` should be `getAllowedFullAccessMembershipStatusIds()` (fixed enum ids).
 */
export function resolveUserAccessState(
  membership: UserMembership | null | undefined,
  tracker: ProfileTracker[] | null | undefined,
  allowedFullAccessStatusIds: number[]
): UserAccessState {
  if (!isMainOnboardingComplete(tracker)) {
    return 'ONBOARDING';
  }
  if (!isApplicationPhaseComplete(tracker)) {
    return 'APPLICATION';
  }
  if (!isMembershipAllowed(membership, allowedFullAccessStatusIds)) {
    return 'BLOCKED';
  }
  return 'ALLOWED';
}

/**
 * Access allowed when `membership_status` FK is in the allowed-id set (accepted / trial).
 */
export function isMembershipAllowed(
  membership: UserMembership | null | undefined,
  allowedFullAccessStatusIds: number[]
): boolean {
  if (membership == null || !allowedFullAccessStatusIds.length) {
    return false;
  }
  const statusId = getMembershipStatusId(membership);
  if (statusId == null) {
    return false;
  }
  return allowedFullAccessStatusIds.includes(statusId);
}

/**
 * Normalized path without query string; no trailing slash except root.
 */
export function normalizeRoutePath(url: string): string {
  const path = url.split('?')[0].split('#')[0];
  if (path.length > 1 && path.endsWith('/')) {
    return path.slice(0, -1);
  }
  return path;
}

/**
 * Routes reachable while membership is BLOCKED (extended whitelist per plan).
 */
export function isAllowedRouteWhileBlocked(normalizedPath: string): boolean {
  const prefixes = [
    '/profile',
    '/welcome',
    '/membership-review',
    '/membership-application',
    '/add-email',
    '/change-email',
    '/verify-email',
    '/add-phone',
    '/change-phone',
    '/verify-phone-number',
    '/account/social/connect',
    '/account/email/verification',
    '/organisation',
  ];
  return prefixes.some((p) => normalizedPath === p || normalizedPath.startsWith(p + '/'));
}
